import styles from './NotificationsList.module.scss';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useMemo, useState } from 'react';
import TitleSectionBack from '../../../Common/TitleSectionBack';
import { getNotifications } from '../../store/selectors';
import { notificationActions } from '../../store';
import NotificationsTable from '../NotificationsTable';
import SearchInput from '../../../Common/SearchInput';
import { getActiveSalonId } from '../../../Auth/store/selectors';
import useWindowSize from '../../../../utils/useWindowSize';
import clx from 'classnames';
import { CalendarCheck, CalendarX2 } from 'lucide-react';

const NotificationsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const [activeFilterId, setActiveFilterId] = useState(2);
  const [searchedName, setSearchedName] = useState('');
  const activeSalonId = useSelector(getActiveSalonId());
  const notifications = useSelector(getNotifications());
  const [paginationValue, setPaginationValue] = useState({
    skip: 0,
    limit: 10,
  });

  const filtersButtons = useMemo(
    () => [
      {
        name: t('notifications.filters.cancellations'),
        id: 2,
        icon: CalendarX2,
      },
      {
        name: t('notifications.filters.bookings'),
        id: 1,
        icon: CalendarCheck,
      },
    ],
    [t],
  );

  useEffect(() => {
    activeSalonId &&
      activeFilterId &&
      dispatch(
        notificationActions.GET_NOTIFICATIONS.REQUEST({
          query: {
            notification_type: activeFilterId,
            skip: paginationValue.skip,
            limit: paginationValue.limit,
          },
        }),
      );
  }, [dispatch, activeSalonId, activeFilterId, paginationValue.skip, paginationValue.limit]);

  const filteredValues = (notifications?.notificationsList || []).filter(
    (notification) =>
      !searchedName ||
      notification.clientName?.toLowerCase().includes(searchedName.toLowerCase()),
  );

  return (
    <div
      className={clx(styles.notification_list_container, {
        [styles.notification_list_container_mobile]: isMobile,
      })}
    >
      <div className={styles.header}>
        <TitleSectionBack
          title={t('notifications.title')}
          description={t('notifications.description')}
        />
      </div>

      <div className={styles.list_card}>
        <div className={styles.toolbar}>
          <div className={styles.filters_row}>
            {filtersButtons.map((filter) => {
              const Icon = filter.icon;
              const isActive = activeFilterId === filter.id;
              return (
                <button
                  key={filter.id}
                  type="button"
                  className={clx(styles.filter_chip, {
                    [styles.filter_chip_selected]: isActive,
                  })}
                  aria-pressed={isActive}
                  onClick={() => {
                    setActiveFilterId(filter.id);
                    setPaginationValue({ skip: 0, limit: 10 });
                  }}
                >
                  <span className={styles.filter_chip_icon}>
                    <Icon size={16} strokeWidth={2.2} />
                  </span>
                  <span className={styles.filter_chip_name}>{filter.name}</span>
                </button>
              );
            })}
          </div>
          <div className={styles.toolbar_actions}>
            <div className={styles.search_wrap}>
              <SearchInput
                placeholder={t('notifications.search.placeholder')}
                value={searchedName}
                setValue={setSearchedName}
                backgroundColor={'white'}
                fullWidth={isMobile}
                compact
              />
            </div>
          </div>
        </div>
        <NotificationsTable
          tableName={filtersButtons.find((filter) => filter.id === activeFilterId)?.name}
          activeFilterId={activeFilterId}
          setPaginationValue={setPaginationValue}
          pageCount={Math.ceil((notifications?.totalNotifications || 0) / paginationValue.limit)}
          isMobile={isMobile}
          values={filteredValues}
        />
      </div>
    </div>
  );
};

export default NotificationsList;
